import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { AttributionTracker } from '../AttributionTracker'
import { GuideLeadPopup } from '../GuideLeadPopup'
import { WhatsAppSuspensionPopup } from '../WhatsAppSuspensionPopup'
import { Layout } from './Layout'

function ScrollToPageTop() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    let cancelled = false
    const timeouts: number[] = []

    const scrollToHash = () => {
      if (cancelled || !hash) return false
      const target = document.getElementById(decodeURIComponent(hash.slice(1)))
      if (!target) return false
      target.scrollIntoView({ behavior: 'instant', block: 'start' })
      return true
    }

    const frame = window.requestAnimationFrame(() => {
      if (cancelled) return
      if (hash) {
        if (scrollToHash()) return
        // Lazy routes (like /about) may not have mounted the target yet.
        ;[50, 150, 400, 800].forEach((ms) => {
          timeouts.push(window.setTimeout(() => { scrollToHash() }, ms))
        })
        return
      }

      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    })

    return () => {
      cancelled = true
      window.cancelAnimationFrame(frame)
      timeouts.forEach(clearTimeout)
    }
  }, [pathname, hash])

  return null
}

export function RootLayout() {
  const { pathname } = useLocation()
  return (
    <>
      {/* Rendered first so vite-react-ssg's collected <Head> tags (which land
          ahead of index.html's own static <head> content, charset included)
          put charset before title/meta/canonical instead of ~1.3KB into <head>. */}
      <Head>
        <meta charSet="utf-8" />
      </Head>
      {/*
        Baked in at prerender time with the path the SSG *thinks* it's
        rendering. vite-react-ssg 0.8.9 can occasionally reuse the homepage's
        rendered body for a different route's HTML file; when that happens,
        this marker (part of the reused body) carries the WRONG path, so
        index.html's inline script can detect the mismatch against the real
        `window.location.pathname` and only then fall back to a full
        client-side re-render — instead of unconditionally discarding every
        non-home route's perfectly good prerendered HTML on every load.
      */}
      <span id="__ssg_path" data-path={pathname} hidden />
      <ScrollToPageTop />
      <AttributionTracker />
      <Layout>
        <Outlet />
      </Layout>
      <GuideLeadPopup />
      <WhatsAppSuspensionPopup />
    </>
  )
}
